import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, MapPin, ShieldCheck, FlaskConical, Building2, AlertCircle, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { STATE_LOCATIONS } from "../data/stateLocations";

const BASE = "https://www.peptidesformedspas.org";

// Peptide categories linked from every state page — keeps structure consistent,
// content unique via state-specific paragraphs.
const CATEGORY_LINKS = [
  { to: "/anti-aging-peptides", label: "Anti-Aging & Skin", detail: "GHK-Cu, Glutathione, NAD+" },
  { to: "/weight-loss-peptides", label: "Weight Loss & Metabolism", detail: "5-Amino-1MQ, GLP-1 context" },
  { to: "/recovery-peptides", label: "Recovery & Repair", detail: "BPC-157, TB-500, PDA" },
  { to: "/hormone-peptides", label: "Hormone & Longevity", detail: "Sermorelin, MOTS-c, α-Klotho" },
  { to: "/sexual-wellness-peptides", label: "Sexual Wellness", detail: "PT-141 / Bremelanotide" },
];

const StateLocationPage = () => {
  const { state: slug } = useParams();
  const data = STATE_LOCATIONS[slug];

  const pageTitle = data
    ? `Med Spa Peptides in ${data.name} | ${data.abbrev} Research Guide`
    : undefined;
  const pageDescription = data
    ? `Research peptides studied in ${data.name} med spa and aesthetic contexts — GHK-Cu, Glutathione, NAD+, BPC-157, TB-500, Sermorelin, PT-141. Includes ${data.name} medical-practice and med-spa regulatory context. Not medical advice.`
    : undefined;

  usePageSEO(pageTitle, pageDescription);

  if (!data) return <Navigate to="/locations" replace />;

  const pageUrl = `${BASE}/locations/${data.slug}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: pageTitle,
      description: pageDescription,
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      about: { "@id": `${BASE}/#organization` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
      speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".state-intro"] },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Locations", item: `${BASE}/locations` },
        { "@type": "ListItem", position: 3, name: data.name, item: pageUrl },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Place",
      "@id": `${pageUrl}#place`,
      name: `Research peptide catalog serving ${data.name}`,
      address: { "@type": "PostalAddress", addressRegion: data.abbrev, addressCountry: "US" },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: `Are research peptides legal to study in ${data.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Research chemicals — including most peptides discussed in med-spa research contexts (GHK-Cu, BPC-157, TB-500, MOTS-c, 5-Amino-1MQ) — are sold for laboratory use only and are not approved by the FDA for human use. Whether a specific product may be handled in ${data.name} depends on federal law, state law, and the specific compound. Always verify with ${data.medicalBoard} and the ${data.name} Board of Pharmacy.`,
          },
        },
        {
          "@type": "Question",
          name: `Do I need a physician to operate a med spa in ${data.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: data.supervisoryRule,
          },
        },
        {
          "@type": "Question",
          name: `Does PeptidesForMedSpas operate a med spa in ${data.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `No. PeptidesForMedSpas is an educational storefront based in Honolulu, HI. We do not prescribe, dispense, or operate medical practices in ${data.name} or any other state. For clinician-supervised peptide-related care, see our Partners page or consult a licensed ${data.name} clinician directly.`,
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid={`state-page-${data.slug}`}>
      <Header />
      <JsonLd id={`state-jsonld-${data.slug}`} data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6" data-testid="state-breadcrumb">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/locations" className="hover:text-[#b89a6d]">Locations</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">{data.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <MapPin className="w-4 h-4 text-[#b89a6d]" />
            <span className="text-sm text-[#b89a6d] font-medium">{data.name} ({data.abbrev}) · Regional Research Guide</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="state-h1">
            Research Peptides for Med Spas in {data.name}
          </h1>
          <p className="state-intro text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            An educational overview of the peptide research categories most often studied in {data.name} med spa and aesthetic contexts, paired with {data.abbrev} medical-practice and med-spa regulatory context. PeptidesForMedSpas is a research-focused catalog — we do not prescribe, dispense, or operate medical practices in {data.name}.
          </p>
        </div>
      </section>

      {/* Peptide categories */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-3">
            Peptide categories studied in {data.name} med spa research
          </h2>
          <p className="text-[#5a4a40] mb-6 max-w-3xl">{data.peptideContext}</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="state-category-links">
            {CATEGORY_LINKS.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`state-cat-${c.to.replace(/\//g, "")}`}
                className="bg-white border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-xl p-5 transition-all group"
              >
                <div className="flex items-start justify-between mb-2">
                  <FlaskConical className="w-5 h-5 text-[#b89a6d]" />
                  <ArrowRight className="w-4 h-4 text-[#b89a6d] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </div>
                <h3 className="text-base font-bold text-[#3a2a26] mb-1">{c.label}</h3>
                <p className="text-xs text-[#7a6b62]">{c.detail}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory context */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <article className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7" data-testid="state-supervisory">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="w-6 h-6 text-[#b89a6d]" />
              <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">{data.abbrev} Medical-Practice & Delegation</h2>
            </div>
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-3">
              <strong>Regulator:</strong> {data.medicalBoard}.
            </p>
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">
              {data.supervisoryRule}
            </p>
          </article>
          <article className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7" data-testid="state-specific">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="w-6 h-6 text-[#b89a6d]" />
              <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">{data.name}-specific considerations</h2>
            </div>
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">
              {data.stateSpecific}
            </p>
          </article>
        </div>
      </section>

      {/* Notable metros */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-5">Notable {data.name} markets</h2>
          <div className="flex flex-wrap gap-3" data-testid="state-metros">
            {data.notableMetros.map((m) => (
              <span key={m} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#f0d8d4] rounded-full text-sm text-[#5a4a40]">
                <Building2 className="w-4 h-4 text-[#b89a6d]" />
                {m}
              </span>
            ))}
          </div>
          <p className="text-xs text-[#a09186] mt-4">
            Markets listed are examples of metropolitan areas within {data.name} where aesthetic and longevity-medicine practices operate. PeptidesForMedSpas does not endorse or refer to specific clinics.
          </p>
        </div>
      </section>

      {/* B2B cross-link */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#fdf2f4] to-[#fffaf5] border border-[#e8c5b8] rounded-2xl p-8">
            <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26] mb-3">
              Running a med spa in {data.name}?
            </h2>
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-5">
              If you operate or direct a licensed med spa, longevity clinic, or aesthetic practice in {data.name}, our B2B resources cover how peptide research education, provider-level sourcing, and compliance framing typically fit together. We do not prescribe or dispense — everything clinical stays with your licensed provider network.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/for-med-spas" data-testid="state-to-b2b-medspas" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold rounded-lg transition-colors text-sm">
                For Med Spa Owners
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/for-providers" data-testid="state-to-b2b-providers" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-[#d4a5ad] text-[#b89a6d] hover:bg-[#fdf2f4] font-semibold rounded-lg transition-colors text-sm">
                For Licensed Providers
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/compliance" data-testid="state-to-compliance" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-[#e8c5b8] text-[#5a4a40] hover:border-[#d4a5ad] font-semibold rounded-lg transition-colors text-sm">
                Compliance Framework
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance banner */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6 text-center">
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              <strong className="text-[#b89a6d]">Educational content only.</strong> This page summarizes {data.name} regulatory context at a high level; it is not legal advice. Research peptides are sold for laboratory use only and are not approved by the FDA for human use. Discuss any wellness or aesthetic intervention with a licensed {data.name} clinician.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StateLocationPage;
